import { useCallback, useEffect, useMemo, useState } from "react";
import type React from "react";
import { api } from "../../api/client";
import { theme, card, emptyFrame, btnPrimary, btnSecondary } from "../../theme";
import { RulerProgress } from "../../components/Drawing";
import { fmtGbp } from "../../utils/format";
import { downloadCsv } from "../../lib/registerExport";
import { gradeStyle } from "../../lib/consultantGrades";
import { fmtDateOnly } from "../../lib/dateOnly";
import { useConsultantShell } from "./ConsultantShellContext";
import { addDaysKey, errText, monthLabel, todayKey, type RevenueReport, type TimeEntry, type UtilisationRow } from "./workspace";

// ── Running the practice ──────────────────────────────────────────────────
// Utilisation (booked slots + logged billable time against capacity), time
// logging for work outside booked slots, and revenue history with a forecast.
// Colleagues' figures and money are for firm administrators; the API enforces it
// (backend routes/consultantWorkspace.routes.ts), these pages only follow.

const page: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 16 };
const heading = (title: string, sub: string) => (
  <div>
    <div style={{ fontSize: 17, fontWeight: 800, color: theme.color.heading, marginBottom: 4 }}>{title}</div>
    <div style={{ fontSize: 13, color: theme.color.subtle }}>{sub}</div>
  </div>
);
const input: React.CSSProperties = { padding: "6px 8px", borderRadius: 3, border: `1px solid ${theme.color.borderStrong}`, background: theme.color.panel, color: theme.color.text, fontSize: 12.5, fontFamily: theme.font };

function monthRange(offset: number): { from: string; to: string; label: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  const k = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { from: k(first), to: k(last), label: first.toLocaleDateString("en-GB", { month: "long", year: "numeric" }) };
}

// ── Utilisation ───────────────────────────────────────────────────────────

export function UtilisationPage() {
  const { isFirmAdmin } = useConsultantShell();
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<UtilisationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const range = monthRange(offset);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    api.get<{ consultants: UtilisationRow[] }>("/consultant/workspace/utilisation", { from: range.from, to: range.to })
      .then((d) => { if (!cancelled) setRows(d.consultants); })
      .catch((e) => { if (!cancelled) setError(errText(e, "Could not load utilisation")); });
    return () => { cancelled = true; };
  }, [range.from, range.to]);

  const totals = useMemo(() => (rows ?? []).reduce((t, r) => ({
    capacity: t.capacity + r.capacityHours, booked: t.booked + r.bookedHours, billable: t.billable + r.loggedBillableHours, other: t.other + r.loggedOtherHours,
  }), { capacity: 0, booked: 0, billable: 0, other: 0 }), [rows]);

  const pct = (u: number | null) => (u === null ? "—" : `${Math.round(u * 100)}%`);
  const tone = (u: number | null) => (u === null ? theme.color.faint : u > 1 ? theme.color.dangerText : u >= 0.75 ? theme.color.successText : u >= 0.4 ? theme.color.warningText : theme.color.subtle);

  return (
    <div style={page}>
      {heading("Utilisation", isFirmAdmin
        ? "Booked consultancy time and billable logged time against each consultant's capacity: 8 hours a weekday, less the days they blocked."
        : "Your booked consultancy time and billable logged time against your capacity. Firm administrators see the whole team.")}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={() => setOffset((o) => o - 1)} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}>Previous</button>
        <span style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, minWidth: 150, textAlign: "center" }}>{range.label}</span>
        <button onClick={() => setOffset((o) => o + 1)} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}>Next</button>
        {offset !== 0 && <button onClick={() => setOffset(0)} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}>This month</button>}
      </div>
      {error && <div style={{ fontSize: 13, color: theme.color.dangerText }}>{error}</div>}
      {!rows && !error && <div style={{ fontSize: 13, color: theme.color.subtle }}>Loading…</div>}
      {rows && (
        <div style={{ ...card, padding: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640, fontSize: 13 }}>
            <thead>
              <tr>
                {["Consultant", "Capacity", "Booked", "Logged billable", "Logged other", "Utilisation"].map((h, i) => (
                  <th key={h} scope="col" style={{ textAlign: i === 0 ? "left" : "right", padding: "9px 12px", fontSize: 11, color: theme.color.faint, borderBottom: `1px solid ${theme.color.line}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const gs = gradeStyle(r.primaryGrade);
                return (
                  <tr key={r.id}>
                    <td style={{ padding: "9px 12px", borderBottom: `1px solid ${theme.color.border}` }}>
                      <span style={{ fontWeight: 600, color: theme.color.heading }}>{r.displayName}</span>
                      {r.primaryGrade && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, padding: "1px 7px", borderRadius: 3, background: gs.bg, color: gs.color }}>{gs.label}</span>}
                    </td>
                    <td style={{ padding: "9px 12px", textAlign: "right", borderBottom: `1px solid ${theme.color.border}`, color: theme.color.subtle }}>{r.capacityHours} h</td>
                    <td style={{ padding: "9px 12px", textAlign: "right", borderBottom: `1px solid ${theme.color.border}` }}>{r.bookedHours} h</td>
                    <td style={{ padding: "9px 12px", textAlign: "right", borderBottom: `1px solid ${theme.color.border}` }}>{r.loggedBillableHours} h</td>
                    <td style={{ padding: "9px 12px", textAlign: "right", borderBottom: `1px solid ${theme.color.border}`, color: theme.color.subtle }}>{r.loggedOtherHours} h</td>
                    <td style={{ padding: "9px 12px", textAlign: "right", borderBottom: `1px solid ${theme.color.border}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                        <RulerProgress value={Math.min(100, Math.round((r.utilisation ?? 0) * 100))} label={`Utilisation of ${r.displayName}`} style={{ width: 80 }} />
                        <span style={{ fontWeight: 700, color: tone(r.utilisation), minWidth: 40 }}>{pct(r.utilisation)}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length > 1 && (
                <tr>
                  <td style={{ padding: "9px 12px", fontWeight: 700, color: theme.color.heading }}>Team</td>
                  <td style={{ padding: "9px 12px", textAlign: "right", color: theme.color.subtle }}>{totals.capacity} h</td>
                  <td style={{ padding: "9px 12px", textAlign: "right" }}>{totals.booked} h</td>
                  <td style={{ padding: "9px 12px", textAlign: "right" }}>{totals.billable} h</td>
                  <td style={{ padding: "9px 12px", textAlign: "right", color: theme.color.subtle }}>{totals.other} h</td>
                  <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 700 }}>{totals.capacity ? pct((totals.booked + totals.billable) / totals.capacity) : "—"}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Time logging ──────────────────────────────────────────────────────────

export function TimePage() {
  const { portfolio, isFirmAdmin, user } = useConsultantShell();
  const [entries, setEntries] = useState<TimeEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState(addDaysKey(todayKey(), -30));
  const [to, setTo] = useState(todayKey());
  const [everyone, setEveryone] = useState(false);
  const [form, setForm] = useState({ organizationId: "", date: todayKey(), hours: "1", billable: true, description: "" });
  const [saving, setSaving] = useState(false);
  // The entry being corrected in the form above (PATCH /consultant/workspace/time/:id); its client stays.
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    api.get<{ entries: TimeEntry[] }>("/consultant/workspace/time", { from, to, userId: everyone ? undefined : user.id })
      .then((d) => setEntries(d.entries))
      .catch((e) => { setError(errText(e, "Could not load time entries")); setEntries([]); });
  }, [from, to, everyone, user.id]);
  useEffect(load, [load]);

  const add = () => {
    const hours = Number(form.hours);
    if (!form.organizationId || !form.description.trim()) return;
    // The server's rules (lib/workRecordDates.ts), said before anything is sent.
    const problem = !form.date ? "Choose the date the work was done."
      : form.date > todayKey() ? "Time is logged for work already done, so the date cannot be in the future."
      : !(hours > 0) ? "Enter the hours worked (more than 0)."
      : hours > 24 ? "No more than 24 hours in a day."
      : Math.abs(hours * 4 - Math.round(hours * 4)) > 1e-9 ? "Hours are logged in quarter hours (0.25, 0.5, 0.75)."
      : null;
    if (problem) { setError(problem); return; }
    setError(null);
    setSaving(true);
    const write = editingId
      ? api.patch(`/consultant/workspace/time/${editingId}`, { date: form.date, hours, billable: form.billable, description: form.description.trim() })
      : api.post("/consultant/workspace/time", { ...form, hours, description: form.description.trim() });
    write
      .then(() => { setEditingId(null); setForm((f) => ({ ...f, date: todayKey(), hours: "1", billable: true, description: "" })); load(); })
      .catch((e) => setError(errText(e, editingId ? "Could not change the entry" : "Could not log the time")))
      .finally(() => setSaving(false));
  };
  const startEdit = (e: TimeEntry) => {
    setEditingId(e.id);
    setForm({ organizationId: e.organizationId, date: e.date, hours: String(e.hours), billable: e.billable, description: e.description });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setForm((f) => ({ ...f, date: todayKey(), hours: "1", billable: true, description: "" }));
  };
  const remove = (id: string) => {
    if (!window.confirm("Delete this time entry? This cannot be undone.")) return;
    api.del(`/consultant/workspace/time/${id}`).then(load).catch((e) => setError(errText(e, "Could not delete the entry")));
  };

  const total = (entries ?? []).reduce((n, e) => n + e.hours, 0);
  const billable = (entries ?? []).filter((e) => e.billable).reduce((n, e) => n + e.hours, 0);
  const clients = portfolio.orgs.filter((o) => o.id !== portfolio.selfOrgId);

  return (
    <div style={page}>
      {heading("Time", "Log work done for a client outside a booked slot — reviews, drafting, preparation. It counts towards utilisation and the revenue view; it is not added to the client's invoice.")}

      <div style={{ ...card, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11.5, color: theme.color.subtle }}>Client
          <select value={form.organizationId} disabled={!!editingId} onChange={(e) => setForm({ ...form, organizationId: e.target.value })} style={{ ...input, minWidth: 180 }}>
            <option value="">Choose…</option>
            {clients.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11.5, color: theme.color.subtle }}>Date
          <input type="date" value={form.date} max={todayKey()} onChange={(e) => setForm({ ...form, date: e.target.value })} style={input} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11.5, color: theme.color.subtle }}>Hours
          <input type="number" min={0.25} max={24} step={0.25} value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} style={{ ...input, width: 80 }} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11.5, color: theme.color.subtle, flex: 1, minWidth: 200 }}>What
          <input value={form.description} maxLength={500} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Reviewed the access control policy" style={input} />
        </label>
        <label style={{ display: "flex", gap: 5, alignItems: "center", fontSize: 12.5, color: theme.color.text, paddingBottom: 6 }}>
          <input type="checkbox" checked={form.billable} onChange={(e) => setForm({ ...form, billable: e.target.checked })} /> Billable
        </label>
        <button onClick={add} disabled={saving || !form.organizationId || !form.description.trim()} style={{ ...btnPrimary, padding: "7px 14px", fontSize: 12.5, opacity: saving || !form.organizationId || !form.description.trim() ? 0.6 : 1 }}>
          {saving ? "Saving…" : editingId ? "Save changes" : "Log time"}
        </button>
        {editingId && <button onClick={cancelEdit} style={{ ...btnSecondary, padding: "7px 14px", fontSize: 12.5 }}>Cancel</button>}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ fontSize: 12, color: theme.color.subtle, display: "flex", gap: 4, alignItems: "center" }}>From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={input} /></label>
        <label style={{ fontSize: 12, color: theme.color.subtle, display: "flex", gap: 4, alignItems: "center" }}>To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={input} /></label>
        {isFirmAdmin && (
          <label style={{ fontSize: 12.5, color: theme.color.text, display: "flex", gap: 5, alignItems: "center" }}>
            <input type="checkbox" checked={everyone} onChange={(e) => setEveryone(e.target.checked)} /> Whole team
          </label>
        )}
        <span style={{ fontSize: 12.5, color: theme.color.subtle, marginLeft: "auto" }}>{total} h logged · {billable} h billable</span>
        {entries && entries.length > 0 && (
          <button onClick={() => downloadCsv<TimeEntry>("time", [
            { header: "Date", value: (e) => e.date }, { header: "Consultant", value: (e) => e.userName },
            { header: "Client", value: (e) => e.organizationName }, { header: "Hours", value: (e) => e.hours },
            { header: "Billable", value: (e) => (e.billable ? "yes" : "no") }, { header: "Description", value: (e) => e.description },
          ], entries)} style={{ ...btnSecondary, padding: "5px 12px", fontSize: 12 }}>Export CSV</button>
        )}
      </div>

      {error && <div role="alert" style={{ fontSize: 12.5, color: theme.color.dangerText }}>{error}</div>}
      {entries === null && <div style={{ fontSize: 13, color: theme.color.subtle }}>Loading…</div>}
      {entries && entries.length === 0 && <div style={{ ...emptyFrame, fontSize: 13, color: theme.color.subtle, padding: 28 }}>No time logged in this period.</div>}
      {entries && entries.length > 0 && (
        <div style={{ ...card, padding: 0 }}>
          {entries.map((e, i) => (
            <div key={e.id} style={{ display: "flex", gap: 12, alignItems: "center", padding: "10px 16px", borderBottom: i < entries.length - 1 ? `1px solid ${theme.color.border}` : "none", flexWrap: "wrap" }}>
              <span style={{ width: 88, fontSize: 12, color: theme.color.subtle }}>{fmtDateOnly(`${e.date}T00:00:00.000Z`)}</span>
              <span style={{ flex: 1, minWidth: 180 }}>
                <span style={{ display: "block", fontSize: 13, color: theme.color.text }}>{e.description}</span>
                <span style={{ display: "block", fontSize: 11.5, color: theme.color.faint }}>{e.organizationName}{everyone ? ` · ${e.userName}` : ""}</span>
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>{e.hours} h</span>
              <span style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 8px", borderRadius: 3, background: e.billable ? theme.color.successBg : theme.color.panelAlt, color: e.billable ? theme.color.successText : theme.color.subtle }}>{e.billable ? "Billable" : "Non-billable"}</span>
              {(e.userId === user.id || isFirmAdmin) && (
                <>
                  <button onClick={() => startEdit(e)} disabled={editingId === e.id} style={{ ...btnSecondary, padding: "3px 10px", fontSize: 11.5 }}>Edit</button>
                  <button onClick={() => remove(e.id)} style={{ ...btnSecondary, padding: "3px 10px", fontSize: 11.5 }}>Delete</button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Revenue (firm administrators) ─────────────────────────────────────────

export function RevenuePage() {
  const { portfolio } = useConsultantShell();
  const [data, setData] = useState<RevenueReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [months, setMonths] = useState(12);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get<RevenueReport>("/consultant/workspace/revenue", { months: String(months) })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(errText(e, "Could not load revenue")); });
    return () => { cancelled = true; };
  }, [months]);

  const max = Math.max(1, ...(data?.months ?? []).map((m) => m.bookedPence + m.loggedPence), ...(data?.forecast ?? []).map((f) => f.confirmedPence + f.pendingPence));
  const summary = portfolio.summary;
  const bar = (value: number, color: string) => <div style={{ height: `${(value / max) * 100}%`, background: color, minHeight: value > 0 ? 2 : 0 }} />;

  const list = (title: string, rows: Array<{ id?: string; name?: string; label?: string; pence: number }>) => (
    <div style={{ ...card }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>{title}</div>
      {rows.length === 0 && <div style={{ fontSize: 13, color: theme.color.subtle }}>Nothing yet.</div>}
      {rows.slice(0, 10).map((r, i) => (
        <div key={r.id ?? r.label ?? i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderTop: i ? `1px solid ${theme.color.border}` : "none" }}>
          <span style={{ color: theme.color.text }}>{r.name ?? r.label}</span>
          <span style={{ fontWeight: 700, color: theme.color.heading }}>{fmtGbp(r.pence)}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div style={page}>
      {heading("Revenue", "Consultancy time by month — confirmed bookings, plus billable logged time valued at each consultant's grade rate — with the next three months' bookings as a forecast.")}
      {summary && summary.totalMonthlyBilledPence !== null && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {[
            { label: "Billed this month", value: summary.totalMonthlyBilledPence },
            { label: "Your consultancy revenue this month", value: summary.totalMonthlyConsultancyRevenuePence },
            { label: "Platform fees this month", value: summary.totalMonthlyAbacasRevenuePence },
          ].map((t) => (
            <div key={t.label} style={{ ...card, flex: 1, minWidth: 180, padding: "12px 16px" }}>
              <div style={{ fontSize: 11, color: theme.color.subtle }}>{t.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: theme.color.heading, marginTop: 4 }}>{fmtGbp(t.value ?? 0)}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        {[6, 12, 24].map((m) => (
          <button key={m} onClick={() => setMonths(m)} aria-pressed={months === m}
            style={{ padding: "5px 12px", borderRadius: 3, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${months === m ? theme.color.accent : theme.color.border}`, background: months === m ? theme.color.accentBg : "transparent", color: months === m ? theme.color.accentText : theme.color.subtle }}>
            {m} months
          </button>
        ))}
        {data && (
          <button onClick={() => downloadCsv("revenue", [
            { header: "Month", value: (r: RevenueReport["months"][number]) => r.month },
            { header: "Booked (GBP)", value: (r) => (r.bookedPence / 100).toFixed(2) },
            { header: "Logged time (GBP)", value: (r) => (r.loggedPence / 100).toFixed(2) },
          ], data.months)} style={{ ...btnSecondary, padding: "5px 12px", fontSize: 12, marginLeft: "auto" }}>Export CSV</button>
        )}
      </div>
      {error && <div style={{ fontSize: 13, color: theme.color.dangerText }}>{error}</div>}
      {!data && !error && <div style={{ fontSize: 13, color: theme.color.subtle }}>Loading…</div>}
      {data && (
        <>
          <div style={{ ...card }}>
            <div role="img" aria-label="Monthly consultancy revenue, history and forecast" style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 180 }}>
              {data.months.map((m) => (
                <div key={m.month} title={`${monthLabel(m.month)}: ${fmtGbp(m.bookedPence)} booked, ${fmtGbp(m.loggedPence)} logged time`}
                  style={{ flex: 1, height: "100%", display: "flex", flexDirection: "column-reverse" }}>
                  {bar(m.bookedPence, theme.color.accent)}
                  {bar(m.loggedPence, theme.color.consultant)}
                </div>
              ))}
              {data.forecast.map((f) => (
                <div key={f.month} title={`${monthLabel(f.month)} forecast: ${fmtGbp(f.confirmedPence)} confirmed, ${fmtGbp(f.pendingPence)} awaiting confirmation`}
                  style={{ flex: 1, height: "100%", display: "flex", flexDirection: "column-reverse", opacity: 0.55 }}>
                  {bar(f.confirmedPence, theme.color.accent)}
                  {bar(f.pendingPence, theme.color.faint)}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              {[...data.months.map((m) => m.month), ...data.forecast.map((f) => f.month)].map((k) => (
                <div key={k} style={{ flex: 1, fontSize: 10, color: theme.color.faint, textAlign: "center", overflow: "hidden", whiteSpace: "nowrap" }}>{monthLabel(k)}</div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10, fontSize: 11.5, color: theme.color.subtle }}>
              <span><span style={{ display: "inline-block", width: 10, height: 10, background: theme.color.accent, marginRight: 5, verticalAlign: "middle" }} />Booked time</span>
              <span><span style={{ display: "inline-block", width: 10, height: 10, background: theme.color.consultant, marginRight: 5, verticalAlign: "middle" }} />Logged billable time</span>
              <span><span style={{ display: "inline-block", width: 10, height: 10, background: theme.color.faint, marginRight: 5, verticalAlign: "middle" }} />Forecast awaiting confirmation (faded bars are future months)</span>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
            {list("By client", data.byClient)}
            {list("By consultant", data.byConsultant)}
            {list("By grade", data.byGrade)}
          </div>
        </>
      )}
    </div>
  );
}
